import {
  ArrowBendDownLeftIcon,
  ArrowsLeftRightIcon,
  ClipboardTextIcon,
  CoinsIcon,
  MoneyIcon,
  PackageIcon,
  ShoppingCartSimpleIcon,
  TruckIcon,
  UserCircleIcon,
} from '@phosphor-icons/react'

import { deliveryMethodNames, deliveryMethods, lineItemRejectReasons } from '@reveni-io/reveni-ui'

import {
  anyOfMatchTypes,
  displayMatchTypes,
  fullTextMatchTypes,
  idMatchTypes,
  numberMatchTypes,
  numericComparisonMatchTypes,
  regionMatchTypes,
} from '@/common/components/MatchTypeEditor/constants'
import {
  paymentStatusOptions,
  refundMethodOptions,
  refundStatusOptions,
  shipmentTypeOptions,
  transactionStatuses,
} from '@/common/constants'
import { getHierarchicalReasonMenuItems } from '@/common/helpers/helpers'
import {
  filterDisabledProperties,
  getBaseProductProperties,
  getItemRequestTypeProperty,
} from '@/common/helpers/rules/helpers'

import { getShipmentStatusOptions } from '../helpers'

const mapToComboItems = (items = []) => items.map(item => ({ value: item.id, label: item.internal_name || item.name }))

const getReturnMethodTypeOptions = t =>
  Object.entries(deliveryMethods)
    .filter(([key]) => key !== deliveryMethods.custom)
    .map(([key]) => ({ value: key, label: t(deliveryMethodNames[key]) }))

export const getConditionTypes = ({
  productTypes,
  productCategories,
  productCollections,
  locations,
  carriers,
  carrierServices,
  returnReasons,
  regions,
  language,
  disabledConditionFields = [],
  t,
}) => {
  const conditionTypes = {
    order: {
      label: 'Pedido',
      icon: ShoppingCartSimpleIcon,
      scope: 'order',
      properties: {
        order_name: { label: 'Número de pedido', matchTypes: [...numberMatchTypes, displayMatchTypes.valueInRange] },
        order_date: {
          label: 'Fecha de pedido',
          matchTypes: [
            displayMatchTypes.olderThan,
            displayMatchTypes.newerThan,
            displayMatchTypes.dateInRange,
            displayMatchTypes.notDateInRange,
          ],
        },
        region: { label: 'Región del pedido', matchTypes: regionMatchTypes, comboItems: regions },
        status: { label: 'Estado del pedido', matchTypes: idMatchTypes },
        order_type: {
          label: 'Tipo de pedido',
          matchTypes: idMatchTypes,
          comboItems: [
            { value: 'initial', label: t('Pedido inicial') },
            { value: 'exchange', label: t('Pedido de cambio') },
          ],
        },
        order_tag: { label: 'Etiqueta del pedido', matchTypes: fullTextMatchTypes },
        order_payment_gateway: { label: 'Pasarela de pago del pedido', matchTypes: fullTextMatchTypes },
        order_channel: { label: 'Canal del pedido', matchTypes: fullTextMatchTypes },
        order_source: { label: 'Origen del pedido', matchTypes: fullTextMatchTypes },
        has_discounts: { label: '¿Tiene descuentos aplicados?', matchTypes: [displayMatchTypes.boolean] },
        discount_applied: { label: 'Descuento aplicado', matchTypes: fullTextMatchTypes },
      },
    },
    customer: {
      label: 'Cliente',
      icon: UserCircleIcon,
      scope: 'customer',
      properties: {
        customer_email: { label: 'Email del cliente', matchTypes: fullTextMatchTypes },
        customer_tag: { label: 'Etiqueta del cliente', matchTypes: fullTextMatchTypes },
        customer_group: { label: 'Grupo del cliente', matchTypes: idMatchTypes },
      },
    },
    request: {
      label: 'Transacción',
      description: 'Evalúa las propiedades de la transacción de devolución o cambio.',
      icon: ClipboardTextIcon,
      scope: 'request',
      properties: {
        request_type: {
          label: 'Tipo de transacción',
          matchTypes: idMatchTypes,
          comboItems: [
            { value: 'refund', label: t('Devolución') },
            { value: 'exchange', label: t('Cambio') },
            { value: 'mixed', label: t('Mixta') },
          ],
        },
        request_mode: {
          label: 'Modo de transacción',
          matchTypes: [displayMatchTypes.is, displayMatchTypes.isNot],
          comboItems: [
            { value: 'instant', label: t('Instantáneo') },
            { value: 'non_instant', label: t('No instantáneo') },
          ],
        },
        status: {
          label: 'Estado de la transacción',
          matchTypes: idMatchTypes,
          comboItems: transactionStatuses.map(s => ({ value: s.value, label: t(s.label) })),
        },
        request_tag: { label: 'Etiqueta de transacción', matchTypes: fullTextMatchTypes },
        has_gifts: { label: '¿Contiene regalos?', matchTypes: [displayMatchTypes.boolean] },
        return_method_type: {
          label: 'Método de devolución',
          matchTypes: idMatchTypes,
          comboItems: getReturnMethodTypeOptions(t),
        },
        return_region: { label: 'Región de devolución', matchTypes: regionMatchTypes, comboItems: regions },
      },
    },
    refund: {
      label: 'Reembolso',
      description:
        'Evalúa las propiedades del importe pendiente de reembolsar en la transacción de devolución o cambio.',
      icon: CoinsIcon,
      scope: 'refund',
      properties: {
        refund_method: {
          label: 'Método de reembolso',
          matchTypes: idMatchTypes,
          comboItems: refundMethodOptions.map(o => ({ value: o.value, label: t(o.label) })),
        },
        refund_status: {
          label: 'Estado del reembolso',
          matchTypes: idMatchTypes,
          comboItems: refundStatusOptions.map(o => ({ value: o.value, label: t(o.label) })),
        },
        refund_total_amount: {
          label: 'Importe total del reembolso',
          matchTypes: numericComparisonMatchTypes,
        },
      },
    },
    payment: {
      label: 'Pago',
      description:
        'Evalúa las propiedades del importe que el cliente aún debe en la transacción de devolución o cambio.',
      icon: MoneyIcon,
      scope: 'payment',
      properties: {
        payment_status: {
          label: 'Estado del pago',
          matchTypes: [displayMatchTypes.is, displayMatchTypes.isNot],
          comboItems: paymentStatusOptions.map(o => ({ value: o.value, label: t(o.label) })),
        },
        payment_total_amount: {
          label: 'Importe total del pago',
          matchTypes: numericComparisonMatchTypes,
        },
      },
    },
    shipment: {
      label: 'Envío',
      icon: TruckIcon,
      scope: 'shipment',
      properties: {
        number: { label: 'Número de envío', matchTypes: [...numberMatchTypes, displayMatchTypes.valueInRange] },
        shipment_type: {
          label: 'Tipo de envío',
          matchTypes: idMatchTypes,
          comboItems: shipmentTypeOptions.map(o => ({ value: o.value, label: t(o.label) })),
        },
        status: {
          label: 'Estado del envío',
          matchTypes: idMatchTypes,
          comboItems: getShipmentStatusOptions().map(option => ({ value: option.value, label: t(option.label) })),
        },
        origin_region: { label: 'Región de origen', matchTypes: regionMatchTypes, comboItems: regions },
        origin_location: {
          label: 'Ubicación de origen',
          matchTypes: idMatchTypes,
          comboItems: mapToComboItems(locations),
        },
        destination_region: { label: 'Región de destino', matchTypes: regionMatchTypes, comboItems: regions },
        destination_location: {
          label: 'Ubicación de destino',
          matchTypes: idMatchTypes,
          comboItems: mapToComboItems(locations),
        },
        carrier: { label: 'Transportista', matchTypes: idMatchTypes, comboItems: mapToComboItems(carriers) },
        logistics_service: {
          label: 'Servicio logístico',
          matchTypes: idMatchTypes,
          comboItems: mapToComboItems(carrierServices),
        },
        tracking_number: {
          label: 'Número de seguimiento',
          matchTypes: [...numberMatchTypes, displayMatchTypes.valueInRange],
        },
      },
    },
    productToReturn: {
      label: 'Producto a devolver',
      icon: ArrowBendDownLeftIcon,
      scope: 'item',
      itemContext: 'to_be_returned',
      supportsAppliesWhen: true,
      properties: {
        ...getBaseProductProperties({ productTypes, productCategories, productCollections }),
        reason: {
          label: 'Motivo de devolución',
          matchTypes: anyOfMatchTypes,
          hierarchical: true,
          comboItems: getHierarchicalReasonMenuItems(returnReasons || [], language),
        },
        reject_reason: {
          label: 'Motivo de rechazo',
          matchTypes: idMatchTypes,
          comboItems: lineItemRejectReasons.map(r => ({ value: r.value, label: t(r.label) })),
        },
        item_request_type: getItemRequestTypeProperty(t),
      },
    },
    newRequestedProduct: {
      label: 'Nuevo producto solicitado',
      icon: ArrowsLeftRightIcon,
      scope: 'item',
      itemContext: 'new_requested',
      supportsAppliesWhen: true,
      properties: getBaseProductProperties({ productTypes, productCategories, productCollections }),
    },
    productInShipment: {
      label: 'Producto en envío',
      icon: PackageIcon,
      scope: 'item',
      itemContext: 'in_order',
      supportsAppliesWhen: true,
      properties: getBaseProductProperties({ productTypes, productCategories, productCollections }),
    },
  }

  return filterDisabledProperties(conditionTypes, disabledConditionFields)
}
